import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  InputBase,
  IconButton,
  Divider,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControlLabel,
  Checkbox,
  Card,
  CardContent,
  CardActions,
  Button,
  Menu,
  MenuItem,
  Stack,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TablePagination,
  Radio,
  RadioGroup,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditIcon from '@mui/icons-material/Edit';
import ArchiveIcon from '@mui/icons-material/Archive';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { getAllDDQQuestions, getTags, updateQuestion } from '../services/api';
import { useDebounce } from '../hooks/useDebounce';

const AnswerLibrary: React.FC = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState<string>('');
  const debouncedSearchQuery = useDebounce(searchQuery, 500);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [tagFilters, setTagFilters] = useState<string[]>([]);
  const [questions, setQuestions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [answeredBy, setAnsweredBy] = useState<string>('');
  const [selectedStatus, setSelectedStatus] = useState<string>('completed');
  const [selectedQuestion, setSelectedQuestion] = useState<any | null>(null);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [questionsPerPage, setQuestionsPerPage] = useState(10);
  const [isTagDialogOpen, setIsTagDialogOpen] = useState(false);
  const [currentTags, setCurrentTags] = useState<string[]>([]);
  const [tagInputValue, setTagInputValue] = useState('');
  const [isSavingTags, setIsSavingTags] = useState(false);

  useEffect(() => {
    const fetchQuestions = async () => {
      setLoading(true);
      try {
        let aiGenerated: boolean | undefined = undefined;
        if (answeredBy === 'ai') {
          aiGenerated = true;
        } else if (answeredBy === 'user') {
          aiGenerated = false;
        }
        const data = await getAllDDQQuestions(selectedStatus ? [selectedStatus] : [], aiGenerated, selectedDate || undefined, debouncedSearchQuery, page + 1, questionsPerPage, selectedTags);
        setQuestions(data.questions || []);
        setTotalQuestions(data.total || 0);
        setError(null);
      } catch (err) {
        setError('Failed to fetch questions.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchQuestions();

    const fetchTags = async () => {
      try {
        const tagsData = await getTags();
        setTagFilters(tagsData.tags || []);
      } catch (err) {
        console.error('Failed to fetch tags:', err);
      }
    };

    fetchTags();
  }, [answeredBy, selectedStatus, selectedDate, debouncedSearchQuery, page, questionsPerPage, selectedTags]);

  const handlePageChange = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setQuestionsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedDate(event.target.value || null);
  };

  const handleStatusChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedStatus(event.target.value);
  };

  const handleResetFilters = () => {
    setSelectedTags([]);
    setAnsweredBy('');
    setSelectedStatus('completed');
    setSelectedDate(null);
    setSearchQuery('');
  };

  const handleAnsweredByChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAnsweredBy(event.target.value);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>, question: any) => {
    setAnchorEl(event.currentTarget);
    setSelectedQuestion(question);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedQuestion(null);
  };

  const handleTagToggle = (tag: string) => {
    setSelectedTags(prev =>
      prev.includes(tag)
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    );
  };

  const handleViewHistory = async () => {
    if (selectedQuestion && selectedQuestion.answers && selectedQuestion.answers.length > 0) {
      navigate(`/version-history/${selectedQuestion.answers[0].id}`, {
        state: {
          questionText: selectedQuestion.question_text,
        },
      });
    }
    handleMenuClose();
  };

  const handleOpenTagDialog = () => {
    if (selectedQuestion) {
      setCurrentTags(selectedQuestion.tags || []);
      setIsTagDialogOpen(true);
    }
    setAnchorEl(null);
  };

  const handleCloseTagDialog = () => {
    setIsTagDialogOpen(false);
    setTagInputValue('');
    setCurrentTags([]);
    setSelectedQuestion(null);
  };

  const handleSaveTags = async () => {
    if (selectedQuestion) {
      setIsSavingTags(true);
      try {
        await updateQuestion(selectedQuestion.id, currentTags);
        
        // Refetch questions to show updated tags
        setLoading(true);
        try {
          let aiGenerated: boolean | undefined = undefined;
          if (answeredBy === 'ai') aiGenerated = true;
          else if (answeredBy === 'user') aiGenerated = false;
          const data = await getAllDDQQuestions(selectedStatus ? [selectedStatus] : [], aiGenerated, selectedDate || undefined, debouncedSearchQuery, page + 1, questionsPerPage, selectedTags);
          setQuestions(data.questions || []);
          setTotalQuestions(data.total || 0);
        } catch (err) {
          setError('Failed to fetch questions.');
        } finally {
          setLoading(false);
        }

        handleCloseTagDialog();
      } catch (error) {
        console.error('Failed to update tags:', error);
        setError('Failed to update tags.');
      } finally {
        setIsSavingTags(false);
      }
    }
  };

  const handleAddTag = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && tagInputValue.trim()) {
      event.preventDefault();
      const newTag = tagInputValue.trim();
      if (!currentTags.includes(newTag)) {
        setCurrentTags([...currentTags, newTag]);
      }
      setTagInputValue('');
    }
  };

  const handleDeleteTag = (tagToDelete: string) => {
    setCurrentTags(currentTags.filter(tag => tag !== tagToDelete));
  };


  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={() => navigate('/ddq')} sx={{ mr: 1 }}>
            <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4">
          Answer Library
        </Typography>
      </Box>

      {/* Search Bar */}
      <Paper
        component="div"
        sx={{ p: '2px 4px', display: 'flex', alignItems: 'center', mb: 3 }}
      >
        <SearchIcon sx={{ p: '10px' }} />
        <InputBase
          sx={{ ml: 1, flex: 1 }}
          placeholder="Search"
          value={searchQuery}
          onChange={handleSearchChange}
        />
      </Paper>

      <Box sx={{ display: 'flex', gap: 3, flex: 1, overflow: 'hidden' }}>
        {/* Filters Sidebar */}
        <Box sx={{ width: '300px', overflowY: 'auto' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" gutterBottom>
              Filters
            </Typography>
            <Button size="small" onClick={handleResetFilters}>Reset</Button>
          </Box>

          {/* Question Tags */}
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography>Question Tags</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={1}>
                {tagFilters.map((tag) => (
                  <Box key={tag} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedTags.includes(tag)}
                          onChange={() => handleTagToggle(tag)}
                        />
                      }
                      label={tag}
                    />
                  </Box>
                ))}
              </Stack>
            </AccordionDetails>
          </Accordion>

          {/* Answered By */}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography>Answered By</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <RadioGroup
                value={answeredBy}
                onChange={handleAnsweredByChange}
              >
                <FormControlLabel value="ai" control={<Radio />} label="AI" />
                <FormControlLabel value="user" control={<Radio />} label="User" />
              </RadioGroup>
            </AccordionDetails>
          </Accordion>

          {/* Status */}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography>Status</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ display: 'flex', flexDirection: 'column' }}>
              <RadioGroup
                value={selectedStatus}
                onChange={handleStatusChange}
              >
                {['completed', 'in_progress', 'needs_review', 'not_started', 'rejected'].map(status => (
                  <FormControlLabel
                    key={status}
                    value={status}
                    control={<Radio />}
                    label={status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  />
                ))}
              </RadioGroup>
            </AccordionDetails>
          </Accordion>

          {/* Date */}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography>Date</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <InputBase
                type="date"
                value={selectedDate || ''}
                onChange={handleDateChange}
                sx={{
                  border: '1px solid rgba(0, 0, 0, 0.23)',
                  borderRadius: '4px',
                  p: '4px 8px',
                  width: '100%'
                }}
              />
            </AccordionDetails>
          </Accordion>
        </Box>

        {/* Results Area */}
        <Box sx={{ flex: 1, overflowY: 'auto' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="body1">
              {totalQuestions} results
            </Typography>
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Typography color="error">{error}</Typography>
          ) : questions.length > 0 ? (
            <>
              <Stack spacing={2}>
                {questions.map((question) => (
                  <Card key={question.id} variant="outlined">
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="h6" component="div">
                          {question.question_text}
                        </Typography>
                        <Chip
                          label={question.status.replace('_', ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}
                          size="small"
                          color={question.status === "completed" ? "success" : question.status === "needs_review" ? "warning" : question.status === "in_progress" ? "primary" : "default"}
                        />
                      </Box>

                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {question.answers && question.answers.length > 0 ? `${question.answers[0].answer_text.substring(0, 100)}...` : 'No answer available.'}
                      </Typography>

                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          Created: {new Date(question.created_at).toLocaleDateString()}
                        </Typography>
                      </Box>

                      <Box sx={{ mt: 1 }}>
                        {question.tags && question.tags.length > 0 && (
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            {question.tags.map((tag: string) => (
                              <Chip key={tag} label={tag} size="small" />
                            ))}
                          </Box>
                        )}
                      </Box>
                    </CardContent>

                    <Divider />

                    <CardActions>
                      <Button
                        size="small"
                        startIcon={<ContentCopyIcon />}
                        onClick={() => {
                          if (question.answers && question.answers.length > 0) {
                            navigator.clipboard.writeText(question.answers[0].answer_text);
                          }
                        }}
                      >
                        Copy
                      </Button>
                      <Button size="small" onClick={() => navigate(`/ai-drafts/${question.id}`)}>View full answer</Button>
                      <Box sx={{ flexGrow: 1 }} />
                      <IconButton size="small" onClick={() => navigate(`/ai-drafts/${question.id}`)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small">
                        <ArchiveIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, question)}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </CardActions>
                  </Card>
                ))}
              </Stack>
            </>
          ) : (
            <Typography sx={{ textAlign: 'center', mt: 4 }}>
              No questions available for the selected filters.
            </Typography>
          )}
        </Box>
      </Box>

      {totalQuestions > 0 && (
        <TablePagination
          component="div"
          count={totalQuestions}
          page={page}
          onPageChange={handlePageChange}
          rowsPerPage={questionsPerPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={[5, 10, 25]}
        />
      )}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem
          onClick={handleViewHistory}
          disabled={!selectedQuestion?.answers?.length}
        >
          View version history
        </MenuItem>
        {/* <MenuItem onClick={handleMenuClose}>Mark as not reusable</MenuItem> */}
        <MenuItem onClick={handleOpenTagDialog}>Add tags</MenuItem>
      </Menu>

      <Dialog open={isTagDialogOpen} onClose={handleCloseTagDialog} fullWidth maxWidth="sm">
        <DialogTitle>Add/Edit Tags</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1, p: 1, border: '1px solid #ccc', borderRadius: '4px', alignItems: 'center' }}>
            {currentTags.map((tag) => (
              <Chip
                key={tag}
                label={tag}
                onDelete={() => handleDeleteTag(tag)}
                size="small"
              />
            ))}
            <InputBase
              autoFocus
              value={tagInputValue}
              onChange={(e) => setTagInputValue(e.target.value)}
              onKeyDown={handleAddTag}
              placeholder={currentTags.length === 0 ? "Type a tag and press Enter" : ""}
              sx={{ flexGrow: 1, ml: 1 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseTagDialog}>Cancel</Button>
          <Button onClick={handleSaveTags} disabled={isSavingTags}>
            {isSavingTags ? <CircularProgress size={24} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default AnswerLibrary;